import { useMutation } from "@tanstack/react-query";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { useSubscribeCalendar, useSubscribeGroup } from "src/services";
import { useContext } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";

export const useSubscribe = () => {
  const { user, refetchAuth } = useContext(AuthContext);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const { mutateAsync: subscribeCalendar } = useSubscribeCalendar();
  const { mutateAsync: subscribeGroup } = useSubscribeGroup();

  const {
    mutateAsync: subscribe,
    data: subscriptionSuccessful,
    error: subscriptionError,
    isLoading: isLoadingSubscribing,
    ...rest
  } = useMutation(async ({ calendar }) => {
    try {
      if (!calendar) {
        throw new Error("Calendar is missing.");
      }
      const userCreatedGroupsIds = user.createdGroups.map(
        (createdGroup) => createdGroup._id
      );
      // prevents bug: subscribeGroup an own calendar group (at subscribe experience flow from handleAddToGroup)
      if (userCreatedGroupsIds.includes(calendar._id)) return true;
      const typeGroup = calendar?.calendars;
      if (typeGroup) {
        await subscribeGroup({ groupId: calendar._id });
      } else {
        await subscribeCalendar({
          calendarId: calendar._id,
          handle: calendar?.handle,
        });
      }
      customEvent({
        name: "calendar_added_successfully",
        category: "Subscribe",
        label: "Calendar added successfully",
        data: {
          user_id: user._id,
          user_email: user.email,
          calendar_handle: calendar?.handle,
        },
      });
      refetchAuth();
      return true;
    } catch (error) {
      const upgradeType = error.response?.data?.upgradeType;
      // not handle error for resubscribe case
      if (upgradeType !== "ALREADY_SUBSCRIBED") {
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            `An error occurred. Please try again.`,
          "error"
        );
        customEvent({
          name: "calendar_added_error",
          category: "Subscribe",
          label: "Error in adding calendar",
          data: {
            user_id: user._id,
            user_email: user.email,
            calendar_handle: calendar?.handle,
          },
        });
        throw error; // propagate error to useMutation
      }
    }
  });

  return {
    subscribe,
    subscriptionSuccessful,
    subscriptionError,
    isLoadingSubscribing,
    ...rest,
  };
};
